import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { PaginationState } from "./usePagination";

export type CustomerType = "general_contractor" | "property_manager" | "owner" | "insurance_company" | "other";
export type QBSyncStatus = "not_synced" | "pending" | "synced" | "error";

export interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  contact_name: string | null;
  customer_type: string | null;
  billing_address: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_zip: string | null;
  job_address: string | null;
  job_city: string | null;
  job_state: string | null;
  job_zip: string | null;
  qb_customer_id: string | null;
  qb_sync_status: string | null;
  qb_synced_at: string | null;
  notes: string | null;
  logo_url: string | null;
  logo_path: string | null;
  customer_number: number | null;
  created_at: string;
  is_deleted: boolean;
}

export const useCustomers = (pagination?: PaginationState, search?: string) => {
  const { user } = useAuth();
  const searchTerm = search?.trim() || "";

  // Separate count query so pagination totalCount is set outside queryFn
  const { data: totalCount } = useQuery({
    queryKey: ["customers", "count", searchTerm],
    queryFn: async () => {
      let q = supabase
        .from("customers")
        .select("id", { count: "exact", head: true })
        .eq("is_deleted", false);
      if (searchTerm) {
        const numVal = parseInt(searchTerm, 10);
        const numFilter = !isNaN(numVal) ? `,customer_number.eq.${numVal}` : "";
        // Strip non-digits to check if search is a phone number
        const digitsOnly = searchTerm.replace(/\D/g, "");
        const phoneFilter = digitsOnly.length >= 3 ? `,phone.ilike.%${digitsOnly.split("").join("%")}%` : `,phone.ilike.%${searchTerm}%`;
        q = q.or(`name.ilike.%${searchTerm}%,contact_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%${phoneFilter},billing_address.ilike.%${searchTerm}%,billing_city.ilike.%${searchTerm}%${numFilter}`);
      }
      const { count } = await q;
      return count ?? 0;
    },
    enabled: !!user && !!pagination,
  });

  useEffect(() => {
    if (pagination && totalCount != null) {
      pagination.setTotalCount(totalCount);
    }
  }, [totalCount, pagination?.setTotalCount]);

  const qc = useQueryClient();

  // Prefetch next page in the background for seamless pagination
  useEffect(() => {
    if (!user || !pagination || !pagination.canNext) return;
    const nextFrom = pagination.page * pagination.pageSize;
    const nextTo = nextFrom + pagination.pageSize - 1;
    qc.prefetchQuery({
      queryKey: ["customers", pagination.page + 1, pagination.pageSize, searchTerm],
      queryFn: async () => {
        let query = supabase
          .from("customers")
          .select("*")
          .eq("is_deleted", false)
          .order("name");
        if (searchTerm) {
          const numVal = parseInt(searchTerm, 10);
          const numFilter = !isNaN(numVal) ? `,customer_number.eq.${numVal}` : "";
          const digitsOnly = searchTerm.replace(/\D/g, "");
          const phoneFilter = digitsOnly.length >= 3 ? `,phone.ilike.%${digitsOnly.split("").join("%")}%` : `,phone.ilike.%${searchTerm}%`;
          query = query.or(`name.ilike.%${searchTerm}%,contact_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%${phoneFilter},billing_address.ilike.%${searchTerm}%,billing_city.ilike.%${searchTerm}%${numFilter}`);
        }
        query = query.range(nextFrom, nextTo);
        const { data, error } = await query;
        if (error) throw error;
        return (data ?? []) as unknown as Customer[];
      },
      staleTime: 30_000,
    });
  }, [user, pagination?.page, pagination?.pageSize, pagination?.canNext, searchTerm, qc]);

  return useQuery({
    queryKey: ["customers", pagination?.page, pagination?.pageSize, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("customers")
        .select("*")
        .eq("is_deleted", false)
        .order("name");

      if (searchTerm) {
        const numVal = parseInt(searchTerm, 10);
        const numFilter = !isNaN(numVal) ? `,customer_number.eq.${numVal}` : "";
        const digitsOnly = searchTerm.replace(/\D/g, "");
        const phoneFilter = digitsOnly.length >= 3 ? `,phone.ilike.%${digitsOnly.split("").join("%")}%` : `,phone.ilike.%${searchTerm}%`;
        query = query.or(`name.ilike.%${searchTerm}%,contact_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%${phoneFilter},billing_address.ilike.%${searchTerm}%,billing_city.ilike.%${searchTerm}%${numFilter}`);
      }

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as Customer[];
    },
    enabled: !!user,
    placeholderData: keepPreviousData,
  });
};

export interface CreateCustomerInput {
  name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  customer_type?: CustomerType;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  job_address?: string;
  job_city?: string;
  job_state?: string;
  job_zip?: string;
  notes?: string;
}

export const useCreateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCustomerInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;

      const { data: custNumber, error: numErr } = await (supabase.rpc as any)(
        "generate_customer_number",
        { p_company_id: companyId }
      );
      if (numErr) throw numErr;

      const { data, error } = await supabase
        .from("customers")
        .insert({
          company_id: companyId,
          customer_number: custNumber,
          name: input.name,
          contact_name: input.contact_name || null,
          email: input.email || null,
          phone: input.phone || null,
          customer_type: input.customer_type || "general_contractor",
          billing_address: input.billing_address || null,
          billing_city: input.billing_city || null,
          billing_state: input.billing_state || null,
          billing_zip: input.billing_zip || null,
          job_address: input.job_address || null,
          job_city: input.job_city || null,
          job_state: input.job_state || null,
          job_zip: input.job_zip || null,
          notes: input.notes || null,
          qb_sync_status: "pending",
        })
        .select("id, name")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useUpdateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: CreateCustomerInput & { id: string }) => {
      const { error } = await supabase
        .from("customers")
        .update({
          name: input.name,
          contact_name: input.contact_name || null,
          email: input.email || null,
          phone: input.phone || null,
          customer_type: input.customer_type || "general_contractor",
          billing_address: input.billing_address || null,
          billing_city: input.billing_city || null,
          billing_state: input.billing_state || null,
          billing_zip: input.billing_zip || null,
          job_address: input.job_address || null,
          job_city: input.job_city || null,
          job_state: input.job_state || null,
          job_zip: input.job_zip || null,
          notes: input.notes || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useSoftDeleteCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { count } = await supabase
        .from("projects")
        .select("id", { count: "exact", head: true })
        .eq("customer_id", id);
      if (count && count > 0) throw new Error("Customer has active projects — cannot delete");

      const { error } = await supabase
        .from("customers")
        .update({ is_deleted: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};
